import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Download, FileText, Lock, LayoutGrid, List, BarChart3, Plus, ChevronLeft, ChevronRight, RefreshCw, Search, User, Building, Banknote, Eye, X, Edit, Trash2 } from 'lucide-react';
import purchaseRequestService from '../../services/purchaseRequestService';
import { useAuth } from '../../contexts/AuthContext';
import toast from 'react-hot-toast';
import PurchaseReceipt from './PurchaseReceipt';

const PurchaseRequestKanban = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedRequestForReceipt, setSelectedRequestForReceipt] = useState(null);
  const [collapsedColumns, setCollapsedColumns] = useState({});
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [cardPosition, setCardPosition] = useState({ x: 0, y: 0 });
  const [isClosing, setIsClosing] = useState(false);
  const [collapsedCards, setCollapsedCards] = useState({});

  const handleCloseModal = () => {
    setIsClosing(true);
    setTimeout(() => {
      setShowDetailModal(false);
      setIsClosing(false);
    }, 350);
  };

  // Kanban columns based on workflow stages
  const columns = [
    {
      id: "pending_approval",
      title: "Manager Approval",
      status: ["pending_approval"],
      color: "bg-blue-50",
      borderColor: "border-blue-200",
      headerColor: "bg-gradient-to-r from-blue-500 to-blue-600",
      icon: FileText,
    },
    {
      id: "procurement",
      title: "Procurement Confirmation",
      status: ["pending_procurement_review"],
      color: "bg-purple-50",
      borderColor: "border-purple-200",
      headerColor: "bg-gradient-to-r from-purple-500 to-purple-600",
      icon: Lock,
    },
    {
      id: "finance",
      title: "Finance Confirmation",
      status: ["pending_finance_approval"],
      color: "bg-indigo-50",
      borderColor: "border-indigo-200",
      headerColor: "bg-gradient-to-r from-indigo-500 to-indigo-600",
      icon: BarChart3,
    },
    {
      id: "gmd",
      title: "GMD Approval",
      status: ["pending_md_approval"],
      color: "bg-orange-50",
      borderColor: "border-orange-200",
      headerColor: "bg-gradient-to-r from-orange-500 to-orange-600",
      icon: Lock,
    },
    {
      id: "payment",
      title: "Pay Vendor",
      status: ["payment_in_progress"],
      color: "bg-pink-50",
      borderColor: "border-pink-200",
      headerColor: "bg-gradient-to-r from-pink-500 to-pink-600",
      icon: Download,
    },
    {
      id: "awaiting_bank",
      title: "Awaiting Bank Payment",
      status: ["awaiting_payment_confirmation"],
      color: "bg-teal-50",
      borderColor: "border-teal-200",
      headerColor: "bg-gradient-to-r from-teal-500 to-teal-600",
      icon: Banknote,
    },
    {
      id: "delivery",
      title: "Request Closed",
      status: ["awaiting_delivery_confirmation", "completed"],
      color: "bg-green-50",
      borderColor: "border-green-200",
      headerColor: "bg-gradient-to-r from-green-500 to-green-600",
      icon: Plus,
    },
    {
      id: "rejected",
      title: "Rejected",
      status: ["rejected"],
      color: "bg-red-50",
      borderColor: "border-red-200",
      headerColor: "bg-gradient-to-r from-red-500 to-red-600",
      icon: X,
    },
  ];

  useEffect(() => {
    fetchRequests();
  }, []);

  // Refresh when navigating back to this page
  useEffect(() => {
    const handleFocus = () => {
      fetchRequests();
    };
    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, []);

  // ESC key to close modal
  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === 'Escape' && showDetailModal) {
        handleCloseModal();
      }
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [showDetailModal]);

  const toggleColumnCollapse = (columnId) => {
    setCollapsedColumns(prev => ({
      ...prev,
      [columnId]: !prev[columnId]
    }));
  };

  const fetchRequests = async () => {
    try {
      setLoading(true);
      // Fetch all user's requests including rejected ones
      const response = await purchaseRequestService.getMyRequests({
        exclude_status: 'completed,cancelled',
        limit: 100
      });
      console.log('Kanban requests data:', response.data.data.requests);
      
      // Map requests and extract profile picture from nested Requester object
      const mappedRequests = (response.data.data.requests || []).map(request => ({
        ...request,
        requester_profile_pic: request.Requester?.cdn_avatar_url || request.Requester?.avatar || null
      }));
      
      setRequests(mappedRequests);
    } catch (error) {
      console.error('Fetch requests error:', error);
      toast.error('Failed to fetch your purchase requests');
    } finally {
      setLoading(false);
    }
  };

  const getRequestsForColumn = (columnStatuses) => {
    return requests.filter(request => 
      columnStatuses.includes(request.status)
    );
  };

  const handleDownloadReceipt = async (request) => {
    try {
      // Fetch full request details if needed
      const response = await purchaseRequestService.getRequestById(request.request_id);
      setSelectedRequestForReceipt(response.data.data.request);
    } catch (error) {
      console.error('Fetch request error:', error);
      toast.error('Failed to load request details for receipt');
    }
  };

  const handleDelete = async (requestId) => {
    if (!window.confirm('Are you sure you want to delete this request? This action cannot be undone.')) {
      return;
    }

    try {
      await purchaseRequestService.deleteRequest(requestId);
      toast.success('Request deleted successfully');
      fetchRequests();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to delete request');
    }
  };

  const handleEdit = async (request) => {
    // Navigate to create page with request data for editing
    navigate('/purchase-requests/new', { 
      state: { 
        editMode: true, 
        requestData: request 
      } 
    });
  };

  const canEditOrDelete = (request) => {
    // Can edit/delete if status is pending_approval (Manager Approval stage)
    return request.status === 'pending_approval';
  };

  const getPriorityColor = (priority) => {
    const colors = {
      'low': 'bg-green-100 text-green-800',
      'medium': 'bg-yellow-100 text-yellow-800',
      'high': 'bg-orange-100 text-orange-800',
      'urgent': 'bg-red-100 text-red-800'
    };
    return colors[priority] || 'bg-gray-100 text-gray-800';
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN',
      minimumFractionDigits: 0
    }).format(amount);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col bg-gray-50">
      {/* Compact Header */}
      <div className="flex-shrink-0 bg-white border-b border-gray-200 px-6 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <h1 className="text-xl font-bold text-gray-900">My Purchase Requests</h1>
            <button
              onClick={fetchRequests}
              disabled={loading}
              className="flex items-center px-3 py-1.5 text-sm border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors"
            >
              <RefreshCw className={`h-4 w-4 mr-1.5 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </div>
          
          <button
            onClick={() => navigate('/purchase-requests/new')}
            className="flex items-center px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors"
          >
            <Plus className="h-4 w-4 mr-2" />
            Create New
          </button>
        </div>
      </div>

      {/* Full Height Kanban Board */}
      <div className="flex-1 overflow-x-auto overflow-y-hidden px-6 py-4">
        <div className="flex gap-4 h-full">{columns.map((column) => {
          const columnRequests = getRequestsForColumn(column.status);
          const isCollapsed = collapsedColumns[column.id];
          const Icon = column.icon;
          
          return (
            <div
              key={column.id}
              className={`flex-shrink-0 transition-all duration-300 ease-in-out ${isCollapsed ? 'w-16' : 'w-80'} ${column.color} border-2 ${column.borderColor} rounded-xl shadow-sm overflow-hidden flex flex-col h-full`}
            >
              {/* Column Header */}
              <div className={`${column.headerColor} text-white ${isCollapsed ? 'px-2 py-3' : 'px-4 py-3'} transition-all duration-300 flex-shrink-0`}>
                {isCollapsed ? (
                  <div className="flex flex-col items-center space-y-2">
                    <button
                      onClick={() => toggleColumnCollapse(column.id)}
                      className="p-1 hover:bg-white/20 rounded transition-colors"
                      title="Expand column"
                    >
                      <ChevronRight className="h-4 w-4" />
                    </button>
                    <div className="flex flex-col items-center">
                      <span className="bg-white/20 backdrop-blur-sm px-1.5 py-1 rounded-full text-xs font-bold mb-2">
                        {columnRequests.length}
                      </span>
                      <div className="writing-mode-vertical text-xs font-semibold whitespace-nowrap" style={{ writingMode: 'vertical-rl', textOrientation: 'mixed' }}>
                        {column.title}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="flex justify-between items-center">
                    <div className="flex items-center gap-2">
                      <Icon className="h-5 w-5" />
                      <h3 className="font-bold text-sm">
                        {column.title}
                      </h3>
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className="bg-white/20 backdrop-blur-sm px-3 py-1 rounded-full text-sm font-bold">
                        {columnRequests.length}
                      </span>
                      <button
                        onClick={() => toggleColumnCollapse(column.id)}
                        className="p-1 hover:bg-white/20 rounded transition-colors"
                        title="Collapse column"
                      >
                        <ChevronLeft className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {/* Column Content */}
              <div className={`transition-all duration-300 ease-in-out flex-1 overflow-y-auto ${isCollapsed ? 'opacity-0 h-0 overflow-hidden p-0' : 'opacity-100 p-3 space-y-3 custom-scrollbar'}`}>
                {columnRequests.map((request) => (
                  <div
                    key={request.id}
                    id={`request-card-${request.id}`}
                    className="group bg-white rounded-xl border border-gray-200 hover:shadow-xl hover:border-blue-300 transition-all duration-200"
                  >
                    {/* Request Header */}
                    <div 
                      onClick={(e) => {
                        if (!e.target.closest('button') && !e.target.closest('a')) {
                          if (collapsedCards[request.id]) {
                            setCollapsedCards(prev => ({ ...prev, [request.id]: false }));
                          } else {
                            const card = e.currentTarget.parentElement;
                            const rect = card.getBoundingClientRect();
                            setCardPosition({ x: rect.left, y: rect.top });
                            setSelectedRequest(request);
                            setShowDetailModal(true);
                          }
                        }
                      }}
                      className="flex justify-between items-start p-4 cursor-pointer"
                    >
                      <div className="flex-1 pr-2">
                        <p className="text-sm font-semibold text-gray-900 line-clamp-2 leading-snug mb-2">
                          {request.item_service_requested}
                        </p>
                        <div className="flex items-center gap-2">
                          {request.requester_profile_pic ? (
                            <img 
                              src={request.requester_profile_pic} 
                              alt={request.requester_name}
                              className="w-6 h-6 rounded-full object-cover flex-shrink-0"
                            />
                          ) : (
                            <div className="w-6 h-6 bg-gradient-to-br from-blue-400 to-blue-600 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
                              {request.requester_name.charAt(0).toUpperCase()}
                            </div>
                          )}
                          <span className="text-xs text-gray-600 font-medium">{request.requester_name}</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <span className={`px-2 py-0.5 rounded-md text-xs font-semibold uppercase tracking-wide ${getPriorityColor(request.priority)}`}>
                          {request.priority}
                        </span>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setCollapsedCards(prev => ({ ...prev, [request.id]: !prev[request.id] }));
                          }}
                          className="p-1 hover:bg-gray-100 rounded transition-colors"
                          title={collapsedCards[request.id] ? "Expand card" : "Collapse card"}
                        >
                          <ChevronLeft className={`h-4 w-4 text-gray-500 transition-transform ${collapsedCards[request.id] ? 'rotate-90' : '-rotate-90'}`} />
                        </button>
                      </div>
                    </div>

                    {/* Collapsible Content */}
                    <div className={`transition-all duration-300 overflow-hidden ${collapsedCards[request.id] ? 'max-h-0' : 'max-h-[500px]'}`}>
                      <div className="px-4 pb-4 space-y-3">
                        {/* Request ID and Department */}
                        <div className="flex items-center justify-between">
                          <Link
                            to={`/purchase-requests/${request.request_id}`}
                            className="text-xs font-bold text-blue-600 bg-blue-50 px-2.5 py-1 rounded-md hover:bg-blue-100"
                          >
                            {request.request_id}
                          </Link>
                          <p className="text-xs text-gray-500">
                            {purchaseRequestService.formatDepartment(request.department)}
                          </p>
                        </div>

                        <div className="flex items-center justify-between pt-3 border-t border-gray-100">
                          <span className="text-sm font-bold text-gray-900">
                            {formatCurrency(request.amount)}
                          </span>
                          <span className="text-xs text-gray-500">
                            {new Date(request.created_at).toLocaleDateString()}
                          </span>
                        </div>

                        {/* Vendor Info */}
                        <div className="pb-3 border-b border-gray-100">
                          <p className="text-xs text-gray-600">
                            <span className="font-medium">Vendor:</span> {request.vendor_name}
                          </p>
                        </div>

                        {/* Rejection Info */}
                        {request.status === 'rejected' && request.rejection_reason && (
                          <div className="p-2 bg-red-50 border-l-4 border-red-500 rounded">
                            <p className="text-xs font-semibold text-red-900">
                              Rejected by: {request.rejected_by || request.WorkflowApprovals?.find(a => a.action === 'reject')?.approver_name || request.rejected_by_name || request.ApprovingManager?.name || 'Unknown'}
                            </p>
                            <p className="text-xs text-red-700 mt-1">{request.rejection_reason}</p>
                          </div>
                        )}

                        {/* Action Buttons */}
                        <div className="flex gap-2">
                          {request.status === 'rejected' ? (
                            <>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigate(`/purchase-requests/${request.request_id}?edit=true`);
                                }}
                                className="flex-1 text-center px-3 py-2 text-xs font-medium bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition-colors"
                              >
                                Edit & Resubmit
                              </button>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  const card = document.getElementById(`request-card-${request.id}`);
                                  const rect = card.getBoundingClientRect();
                                  setCardPosition({ x: rect.left, y: rect.top });
                                  setSelectedRequest(request);
                                  setShowDetailModal(true);
                                }}
                                className="flex items-center justify-center px-3 py-2 text-xs font-medium bg-gray-50 text-gray-600 rounded-lg hover:bg-gray-100 transition-colors"
                                title="View Details"
                              >
                                <Eye className="h-3.5 w-3.5" />
                              </button>
                            </>
                          ) : (
                            <>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  const card = document.getElementById(`request-card-${request.id}`);
                                  const rect = card.getBoundingClientRect();
                                  setCardPosition({ x: rect.left, y: rect.top });
                                  setSelectedRequest(request);
                                  setShowDetailModal(true);
                                }}
                                className="flex-1 text-center px-3 py-2 text-xs font-medium bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition-colors"
                              >
                                View Details
                              </button>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDownloadReceipt(request);
                                }}
                                className="flex items-center justify-center px-3 py-2 text-xs font-medium bg-green-50 text-green-600 rounded-lg hover:bg-green-100 transition-colors"
                                title="Download Receipt"
                              >
                                <Download className="h-3.5 w-3.5" />
                              </button>
                              {canEditOrDelete(request) && (
                                <>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleEdit(request);
                                    }}
                                    className="flex items-center justify-center px-3 py-2 text-xs font-medium bg-yellow-50 text-yellow-600 rounded-lg hover:bg-yellow-100 transition-colors"
                                    title="Edit Request"
                                  >
                                    <Edit className="h-3.5 w-3.5" />
                                  </button>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleDelete(request.request_id);
                                    }}
                                    className="flex items-center justify-center px-3 py-2 text-xs font-medium bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition-colors"
                                    title="Delete Request"
                                  >
                                    <Trash2 className="h-3.5 w-3.5" />
                                  </button>
                                </>
                              )}
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}

                {/* Empty State */}
                {columnRequests.length === 0 && (
                  <div className="text-center py-12 text-gray-400">
                    <Icon className="h-12 w-12 mx-auto mb-3 opacity-30" />
                    <p className="text-sm font-medium">No requests</p>
                  </div>
                )}
              </div>
            </div>
          );
        })}
        </div>
      </div>

      {/* Receipt Modal */}
      {selectedRequestForReceipt && (
        <PurchaseReceipt
          request={selectedRequestForReceipt}
          onClose={() => setSelectedRequestForReceipt(null)}
        />
      )}

      {/* Detail Modal */}
      {showDetailModal && selectedRequest && (
        <>
          <style>{`
            @keyframes flyToCenter {
              0% {
                transform: translate(${cardPosition.x - window.innerWidth/2}px, ${cardPosition.y - window.innerHeight/2}px) scale(0.2) rotateX(45deg);
                opacity: 0;
              }
              60% {
                transform: translate(0, -20px) scale(1.05) rotateX(-5deg);
                opacity: 1;
              }
              100% {
                transform: translate(0, 0) scale(1) rotateX(0deg);
                opacity: 1;
              }
            }
            @keyframes flyBack {
              0% {
                transform: translate(0, 0) scale(1) rotateX(0deg);
                opacity: 1;
              }
              40% {
                transform: translate(0, -20px) scale(1.05) rotateX(-5deg);
                opacity: 1;
              }
              100% {
                transform: translate(${cardPosition.x - window.innerWidth/2}px, ${cardPosition.y - window.innerHeight/2}px) scale(0.2) rotateX(45deg);
                opacity: 0;
              }
            }
            .modal-fly-in {
              animation: flyToCenter 0.4s cubic-bezier(0.34, 1.2, 0.64, 1);
              transform-style: preserve-3d;
              perspective: 1000px;
            }
            .modal-fly-out {
              animation: flyBack 0.35s cubic-bezier(0.34, 1.2, 0.64, 1);
              transform-style: preserve-3d;
              perspective: 1000px;
            }
          `}</style>
          <div 
            className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
            onClick={handleCloseModal}
          >
            <div 
              className={`bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-hidden ${isClosing ? 'modal-fly-out' : 'modal-fly-in'}`}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="bg-gradient-to-r from-blue-600 to-indigo-600 px-6 py-5 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="bg-white/20 p-2 rounded-lg">
                    <FileText className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-white">Request Details</h2>
                    <p className="text-blue-100 text-sm">{selectedRequest.request_id}</p>
                  </div>
                </div>
                <button
                  onClick={handleCloseModal}
                  className="p-2 hover:bg-white/20 rounded-lg transition-all"
                  title="Close"
                >
                  <X className="h-5 w-5 text-white" />
                </button>
              </div>

              {/* Content */}
              <div className="p-6 overflow-y-auto max-h-[calc(90vh-140px)] space-y-5">
                {/* Quick Info Cards */}
                <div className="grid grid-cols-3 gap-3">
                  <div className="bg-blue-50 border-l-4 border-blue-500 rounded-lg p-4">
                    <p className="text-xs text-blue-600 font-medium mb-1">Status</p>
                    <p className="text-sm font-bold text-blue-900 capitalize">{selectedRequest.status?.replace(/_/g, ' ')}</p>
                  </div>
                  <div className="bg-orange-50 border-l-4 border-orange-500 rounded-lg p-4">
                    <p className="text-xs text-orange-600 font-medium mb-1">Priority</p>
                    <p className="text-sm font-bold text-orange-900 capitalize">{selectedRequest.priority}</p>
                  </div>
                  <div className="bg-green-50 border-l-4 border-green-500 rounded-lg p-4">
                    <p className="text-xs text-green-600 font-medium mb-1">Amount</p>
                    <p className="text-sm font-bold text-green-900">₦{parseFloat(selectedRequest.amount).toLocaleString()}</p>
                  </div>
                </div>

                {/* Main Details */}
                <div className="bg-gray-50 rounded-xl p-5 space-y-4">
                  <div>
                    <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2 block">What's being requested?</label>
                    <p className="text-gray-900 font-medium text-base">{selectedRequest.item_service_requested}</p>
                  </div>
                  
                  {selectedRequest.justification && (
                    <div>
                      <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2 block">Why is this needed?</label>
                      <p className="text-gray-700 leading-relaxed">{selectedRequest.justification}</p>
                    </div>
                  )}
                </div>

                {/* People & Organization */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="flex items-start gap-3 bg-white border border-gray-200 rounded-xl p-4 hover:shadow-md transition-shadow">
                    <div className="bg-blue-100 p-2 rounded-lg">
                      <User className="h-5 w-5 text-blue-600" />
                    </div>
                    <div>
                      <label className="text-xs text-gray-500 font-medium block mb-1">Requested by</label>
                      <p className="text-gray-900 font-semibold">{selectedRequest.requester_name}</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-3 bg-white border border-gray-200 rounded-xl p-4 hover:shadow-md transition-shadow">
                    <div className="bg-purple-100 p-2 rounded-lg">
                      <Building className="h-5 w-5 text-purple-600" />
                    </div>
                    <div>
                      <label className="text-xs text-gray-500 font-medium block mb-1">Department</label>
                      <p className="text-gray-900 font-semibold">{selectedRequest.department}</p>
                    </div>
                  </div>
                </div>

                {/* Additional Info */}
                <div className="grid grid-cols-2 gap-4">
                  {selectedRequest.vendor_name && (
                    <div className="bg-white border border-gray-200 rounded-xl p-4">
                      <label className="text-xs text-gray-500 font-medium block mb-1">Vendor</label>
                      <p className="text-gray-900 font-semibold">{selectedRequest.vendor_name}</p>
                    </div>
                  )}
                  <div className="bg-white border border-gray-200 rounded-xl p-4">
                    <label className="text-xs text-gray-500 font-medium block mb-1">Submitted on</label>
                    <p className="text-gray-900 font-semibold">{new Date(selectedRequest.created_at).toLocaleDateString('en-US', { 
                      month: 'short', 
                      day: 'numeric', 
                      year: 'numeric' 
                    })}</p>
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className="bg-gray-50 px-6 py-4 border-t border-gray-200 flex justify-between items-center">
                <button
                  onClick={() => handleDownloadReceipt(selectedRequest)}
                  className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium shadow-sm"
                >
                  <Download className="h-4 w-4" />
                  Download Receipt
                </button>
                <button
                  onClick={handleCloseModal}
                  className="px-5 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium shadow-sm"
                >
                  Got it
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default PurchaseRequestKanban;